/**
 * Campus Federation API Client
 * ─────────────────────────────
 * Place at: src/api/campusAPI.js
 *
 * Changes vs previous version
 * ─────────────────────────────
 * - grouped()         — region-grouped list for the Craigslist-style picker
 * - search(q)         — typeahead across name / short_name / city
 * - requestCampus()   — self-serve "add my school" submission
 * - getRegionProjects() — region-scoped physical-item feed (Phase 5)
 */

import { api } from './index';   // existing axios instance

export const campusAPI = {
  // ── Public (no auth) ────────────────────────────────────────────────────

  /** Flat list — used by CampusSelector (map nav) and any existing callers. */
  list: () =>
    api.get('/campuses'),

  /**
   * Region-grouped list for the Craigslist-style picker.
   * Returns { regions: [{ name, country, campuses: [...] }], totalCampuses }
   */
  grouped: () =>
    api.get('/campuses/grouped'),

  /**
   * Typeahead search across name, short_name, city.
   * Returns { campuses: [...], count, query }
   * @param {string} q — search string (1–100 chars)
   */
  search: (q) =>
    api.get('/campuses/search', { params: { q } }),

  /** Single campus by slug — used for worldwide-mode cluster labels. */
  get: (slug) =>
    api.get(`/campuses/${slug}`),

  // ── Auth required ────────────────────────────────────────────────────────

  getMyCampus: () =>
    api.get('/campuses/me'),

  /**
   * Assign current user to a campus.
   * Pass null to unset (Universal / worldwide mode).
   * @param {number|null} campusId
   */
  setMyCampus: (campusId) =>
    api.put('/campuses/me', { campusId }),

  /**
   * Self-serve "add my school" flow.
   * Creates an inactive campus pending admin approval.
   * Rate-limited: 3 per day per user.
   * @param {{ name: string, city: string, state: string, country: string, region: string }} data
   */
  requestCampus: (data) =>
    api.post('/campuses/request', data),

  // ── Campus-scoped map data ───────────────────────────────────────────────

  getProjects: (campusId, params = {}) =>
    api.get(`/campuses/${campusId}/projects`, { params }),

  getOutboundLines: (campusId) =>
    api.get(`/campuses/${campusId}/outbound-connections`),

  // ── Region-scoped (Phase 5 — physical-item Local / Universal toggle) ─────

  /**
   * Projects scoped to a region label (e.g. "California — Central Coast").
   * @param {string}  region       — must match campuses.region exactly
   * @param {boolean} physicalOnly — if true, only Physical / Hybrid projects
   * @param {number}  limit        — max 1000
   */
  getRegionProjects: (region, physicalOnly = false, limit = 500) =>
    api.get('/campuses/region-projects', {
      params: { region, physical_only: physicalOnly ? 'true' : 'false', limit },
    }),

  // ── Worldwide mode ───────────────────────────────────────────────────────

  getWorldwideProjects: (params = {}) =>
    api.get('/campuses/worldwide/projects', { params }),

  getWorldwideStats: () =>
    api.get('/campuses/worldwide/stats'),
};